import { IMobileInsightsMapData, IPolygon } from "../../Models/Models";
import { IPoint } from "../../Models/IPoint";
import { IStoreScoreRecord } from "../../Models/IRetailMatch";
import { MapTypesEnum } from "../../Enums/MapTypesEnum";
import { IMapLayers } from "../../Models/IMapLayers";

export class MapLayers implements IMapLayers {
  deck: any;
  isVisits: boolean;
  mapType: MapTypesEnum;

  constructor(
      deck: any,
      mapType: MapTypesEnum,
      isHome?: boolean,
      isVisits?: boolean
  ) {
      this.isVisits = isVisits;
      this.mapType = mapType;
      this.deck = deck;
  }

  createBasePolygon = (data: any, id: string) => {
      return {
          id: id,
          data: data,
          stroked: true,
          filled: true,
          opacity: 0.3,
          lineWidthScale: 2,
          pickable: true,
          autoHighlight: true,
      };
  };

  createGpsPolygonLayer = (
      data: IPoint[][],
      id: string,
      callback: (latitude: number, longitude: number) => void,
      currentBenchmarkIndex?: number
  ) => {
      const basePolygon = this.createBasePolygon(data, id);

      return new this.deck.PolygonLayer({
          ...basePolygon,
          getPolygon: (d: IPoint[]) => d.map((point) => [point.x, point.y]),
          getFillColor:
        currentBenchmarkIndex === 1 ? [43, 139, 73] : [28, 225, 128],
          onClick: ({ coordinate }) => {
              callback(coordinate[1], coordinate[0]);
          },
      });
  };

  createRetailMatchPolygonLayer = (
      data: IPolygon[],
      id: string,
      getVolumeAlpha: (externalID: string) => number
  ) => {
      const basePolygon = this.createBasePolygon(data, id);
      basePolygon.lineWidthScale = 12;

      return new this.deck.PolygonLayer({
          ...basePolygon,
          getPolygon: (d: IPolygon) =>
              d.linearRings[0].map((point) => [point.x, point.y]),
          //getFillColor: (d) => [176, 29, 29, getVolumeAlpha(d.externalID)] // RED
          getFillColor: (d) => [43, 139, 73, getVolumeAlpha(d.externalID)], //GREEN
      });
  };

  createScatterplotLayer = (
      scatterplotHomeColor: any[],
      scatterplotWorkColor: any[],
      scatterplotRadius: number,
      scatterplotLineWidth: number,
      scatterplotOpacity: number,
      data: any,
      isHome: boolean
  ) => {
      let color = null;
      if (isHome) {
          color = scatterplotHomeColor;
      } else {
          color = scatterplotWorkColor;
      }

      const getPosition = this.createGetPositionCallback(isHome);

      return new this.deck.ScatterplotLayer({
          id: "deck-gps-scatterplot-layer",
          data,
          opacity: scatterplotOpacity,
          stroked: true,
          filled: true,
          radiusMinPixels: 2,
          radiusMaxPixels: 100,
          lineWidthMinPixels: 1,
          lineWidthMaxPixels: 10,
          getPosition,
          getRadius: scatterplotRadius,
          getLineWidth: scatterplotLineWidth,
          getFillColor: color,
          getLineColor: [0, 0, 0],
      });
  };

  createHeatmapLayer = (
      homeGradient: number[][],
      workGradient: number[][],
      heatmapOpacity: number,
      heatmapRadius: number,
      heatmapIntensity: number,
      heatmapThreshold: number,
      data: any,
      isHome: boolean
  ) => {
      let colorRange = null;
      if (isHome) {
          colorRange = homeGradient;
      } else {
          colorRange = workGradient;
      }

      const getPosition = this.createGetPositionCallback(isHome);

      return new this.deck.HeatmapLayer({
          id: "deck-gps-heatmap-layer",
          data,
          opacity: heatmapOpacity,
          radiusPixels: heatmapRadius,
          intensity: heatmapIntensity,
          threshold: heatmapThreshold,
          colorRange,
          getPosition,
          getWeight: (p: IMobileInsightsMapData) => {
              if (this.isVisits) {
                  return p.visitWeight;
              } else {
                  return p.visitorWeight;
              }
          },
      });
  };

  createHexagonLayer = (
      homeGradient: number[][],
      workGradient: number[][],
      hexOpacity: number,
      hexRadius: number,
      hexUpperPercentile: number,
      hexLowerPercentile: number,
      hexCoverage: number,
      data: any,
      isHome: boolean
  ) => {
      let colorRange = null;
      if (isHome) {
          colorRange = homeGradient;
      } else {
          colorRange = workGradient;
      }

      const getPosition = this.createGetPositionCallback(isHome);

      return new this.deck.HexagonLayer({
          id: "deck-gps-hexagon-layer",
          data: data,
          opacity: hexOpacity,
          getPosition,
          colorRange,
          radius: hexRadius,
          upperPercentile: hexUpperPercentile,
          lowerPercentile: hexLowerPercentile,
          coverage: hexCoverage,
          getColorWeight: (p: IMobileInsightsMapData) => {
              if (this.isVisits) {
                  return p.visitWeight;
              } else {
                  return p.visitorWeight;
              }
          },
          pickable: true,
          autoHighlight: true,
          onHover: ({ layer, object, x, y }) => {
              this.setHexTooltip(object, layer.props.data.length, x, y);
          },
      });
  };

  private setHexTooltip = (object, dataSize, x, y) => {
      const el = document.getElementById("deck-tooltip");
      if (object) {
          const percent = (object.points.length / dataSize) * 100;
          el.innerHTML = `${percent.toFixed(4)}%`;
          el.style.display = "block";
          el.style.left = `${x}px`;
          el.style.top = `${y - 22}px`;
      } else {
          el.style.display = "none";
      }
  };

  private createGetPositionCallback = (isHome: boolean) => {
      switch (this.mapType) {
          case MapTypesEnum.GPSmap:
              return (d: IMobileInsightsMapData) => {
                  if (isHome) {
                      if (d.homeLongitude !== 0 && d.homeLatitude !== 0) {
                          return [d.homeLongitude, d.homeLatitude];
                      } else {
                          return null;
                      }
                  } else {
                      if (d.workLongitude !== 0 && d.workLatitude !== 0) {
                          return [d.workLongitude, d.workLatitude];
                      } else {
                          return null;
                      }
                  }
              };

          case MapTypesEnum.RetailMatchMap:
              return (d: IStoreScoreRecord) => [d.lat, d.lon];

          default:
              return null;
      }
  };
}
